import { useEffect, useState, type MouseEvent } from 'react';
import {
    ClickAwayListener,
    Paper,
    Popper,
    useTheme,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, type PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import {
    ArrowDropDown as ArrowDropDownIcon,
    DateRange as DateRangeIcon,
} from '@mui/icons-material';
import dayjs, { type Dayjs } from 'dayjs';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import Typography from '@/components/ui/Typography/Typography';
import type { DateRangePickerProps } from './IDateRangePicker';
import { styles } from './styles';

const DAY_MS = 86_400_000;

const formatDate = (ts: number | null): string => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    if (
        d.getDate() === now.getDate() &&
        d.getMonth() === now.getMonth() &&
        d.getFullYear() === now.getFullYear()
    ) {
        return 'Today';
    }
    if (d.getFullYear() === now.getFullYear()) {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US');
};

const stopFocusScroll = (e: MouseEvent) => e.preventDefault();

const RangeDay = (props: PickersDayProps<Dayjs>) => (
    <PickersDay {...props} onMouseDown={stopFocusScroll} />
);

const DateRangePicker = ({
    start,
    end,
    onDateRangeChanged,
    disableFuture = false,
}: DateRangePickerProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const [startDate, setSDate] = useState<number | null>(start ?? null);
    const [endDate, setEDate] = useState<number | null>(end ?? null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (start !== startDate) setSDate(start ?? null);
        const incomingEnd = end ? end - DAY_MS : null;
        if (incomingEnd !== endDate) setEDate(incomingEnd);
    }, [start, end]);

    const applyAndClose = (s: number | null, e: number | null) => {
        setSDate(s);
        setEDate(e);
        setOpen(false);
        onDateRangeChanged(s, e ? e + DAY_MS : null);
    };

    const handleChange = (date: Dayjs | null) => {
        if (!date) {
            applyAndClose(null, null);
            return;
        }
        const ts = date.valueOf();
        applyAndClose(ts, ts);
    };

    const handleClear = () => applyAndClose(null, null);

    const emptyLabel = startDate == null && endDate == null ? 'Date' : null;
    const beforeLabel =
        startDate == null && endDate != null ? `Before ${formatDate(endDate)}` : null;
    const afterLabel =
        endDate == null && startDate != null ? `After ${formatDate(startDate)}` : null;
    const betweenLabel =
        startDate === endDate
            ? formatDate(startDate)
            : `${formatDate(startDate)} - ${formatDate(endDate)}`;
    const label = emptyLabel ?? beforeLabel ?? afterLabel ?? betweenLabel;

    return (
        <>
            <Box
                ref={setAnchorEl}
                sx={styles.triggerBox(theme)}
                onClick={() => setOpen((p) => !p)}
            >
                <DateRangeIcon
                    sx={{ color: theme.palette.blue.main, fontSize: 18, flexShrink: 0 }}
                />
                <Typography sx={styles.label}>{label}</Typography>
                <ArrowDropDownIcon sx={{ color: 'black', flexShrink: 0 }} />
            </Box>
            <Popper
                open={open && Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-start"
                modifiers={[
                    { name: 'offset', options: { offset: [0, 8] } },
                    { name: 'preventOverflow', options: { boundary: 'viewport' } },
                ]}
                sx={{ zIndex: theme.zIndex.modal }}
            >
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Paper elevation={4} sx={{ p: 1, borderRadius: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateCalendar
                                disableFuture={disableFuture}
                                value={startDate ? dayjs(startDate) : null}
                                onChange={handleChange}
                                autoFocus={false}
                                slots={{ day: RangeDay }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1 }}>
                                <Button size="small" onClick={handleClear}>
                                    Clear
                                </Button>
                            </Box>
                        </LocalizationProvider>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
};

export default DateRangePicker;
